// OrgDocUpload.js — InvestorQ
// Secure document upload portal for Tier B orgs
// Accessed via email link: investorq.io/upload-docs?token=XXX
//
// File: src/components/OrgDocUpload.js

import { useState, useEffect } from "react";

const API_URL = process.env.REACT_APP_API_URL ||
  "https://financial-analyst-backend-production-7175.up.railway.app/api";

const DOC_TYPES = [
  { key: "financials", label: "Financial Statements", icon: "📊", required: true,
    hint: "2+ years GAAP financials — reviewed or audited by CPA", accept: ".pdf,.xlsx,.xls" },
  { key: "captable",   label: "Ownership / Cap Table", icon: "📋", required: true,
    hint: "Current ownership structure and equity breakdown", accept: ".pdf,.xlsx,.xls" },
  { key: "orgdeck",    label: "Organization Overview / Pitch Deck", icon: "🏆", required: false,
    hint: "League structure, history, growth story", accept: ".pdf" },
  { key: "sponsordeck",label: "Sponsorship / Media Package", icon: "📺", required: false,
    hint: "Sponsor portfolio, media rights, broadcast deals", accept: ".pdf" },
];

export default function OrgDocUpload() {
  // Get token from URL
  const token = new URLSearchParams(window.location.search).get("token");

  const [status, setStatus]       = useState("validating"); // validating|valid|expired|invalid|uploading|success|error
  const [orgData, setOrgData]     = useState(null);
  const [files, setFiles]         = useState({});           // { key: File }
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded]   = useState([]);
  const [error, setError]         = useState("");

  // Validate token on mount
  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }

    fetch(`${API_URL}/orgs/upload-portal?token=${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.expired) { setStatus("expired"); return; }
        if (data.error)   { setStatus("invalid"); return; }
        setOrgData(data);
        setStatus("valid");
      })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const handleFileChange = (key, file) => {
    setFiles(prev => ({ ...prev, [key]: file }));
  };

  const handleUpload = async () => {
    const requiredMissing = DOC_TYPES
      .filter(d => d.required && !files[d.key])
      .map(d => d.label);

    if (requiredMissing.length > 0) {
      setError(`Please upload required documents: ${requiredMissing.join(", ")}`);
      return;
    }

    setUploading(true);
    setError("");

    const formData = new FormData();
    DOC_TYPES.forEach(doc => {
      if (files[doc.key]) {
        formData.append(doc.key, files[doc.key]);
        formData.append(`name_${doc.key}`, doc.label);
        formData.append(`nda_${doc.key}`, doc.key === "financials" || doc.key === "captable" ? "true" : "false");
      }
    });

    try {
      const res = await fetch(`${API_URL}/orgs/${orgData.appId}/upload-docs`, {
        method: "POST",
        headers: { "x-upload-token": token },
        body: formData,
      });
      const data = await res.json();

      if (!res.ok || !data.success) throw new Error(data.error || "Upload failed");

      setUploaded(data.uploaded || []);
      setStatus("success");
    } catch (err) {
      setError(err.message);
    }
    setUploading(false);
  };

  // ── States ────────────────────────────────────────────────────────────────

  if (status === "validating") {
    return (
      <PageShell>
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <div style={{ fontSize: 32, marginBottom: 16, opacity: 0.4 }}>⏳</div>
          <div style={{ fontSize: 14, color: "#94a3b8" }}>Validating your upload link…</div>
        </div>
      </PageShell>
    );
  }

  if (status === "expired") {
    return (
      <PageShell>
        <StatusCard icon="⏰" color="#f5a623" title="Upload Link Expired">
          <p>Your secure upload link has expired. Upload links are valid for 7 days from when they're sent.</p>
          <p>Please email <a href="mailto:info@investorq.io" style={{ color: "#7c6fff" }}>info@investorq.io</a> to request a new upload link.</p>
        </StatusCard>
      </PageShell>
    );
  }

  if (status === "invalid") {
    return (
      <PageShell>
        <StatusCard icon="🔒" color="#ef4444" title="Invalid Upload Link">
          <p>This upload link is invalid or has already been used.</p>
          <p>Please email <a href="mailto:info@investorq.io" style={{ color: "#7c6fff" }}>info@investorq.io</a> if you believe this is an error.</p>
        </StatusCard>
      </PageShell>
    );
  }

  if (status === "success") {
    return (
      <PageShell>
        <div style={{ maxWidth: 560, margin: "0 auto", padding: "40px 20px" }}>
          <div style={{ background: "rgba(30,41,59,0.6)", border: "1px solid rgba(0,212,170,0.3)",
            borderRadius: 20, padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#e2e8f0", marginBottom: 8 }}>Documents Uploaded!</div>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 24, lineHeight: 1.7 }}>
              Thank you! We've received {uploaded.length} document{uploaded.length !== 1 ? "s" : ""} for
              <strong style={{ color: "#94a3b8" }}> {orgData?.orgName}</strong>.
              Our team will review them and get back to you within 3-5 business days.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 28, textAlign: "left" }}>
              {uploaded.map((doc, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "center",
                  padding: "10px 14px", background: "rgba(0,212,170,0.06)",
                  border: "1px solid rgba(0,212,170,0.2)", borderRadius: 10 }}>
                  <span style={{ color: "#00d4aa", fontSize: 14 }}>✓</span>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>{doc.name} ({doc.type})</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: "#475569", lineHeight: 1.7 }}>
              📧 A confirmation has been sent to your registered email.<br />
              Questions? <a href="mailto:info@investorq.io" style={{ color: "#7c6fff" }}>info@investorq.io</a>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  // ── Main upload form ────────────────────────────────────────────────────────
  const expiryDate = orgData?.expires ? new Date(orgData.expires).toLocaleDateString() : "—";

  return (
    <PageShell>
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "32px 20px 60px" }}>

        {/* Header */}
        <div style={{ background: "rgba(30,41,59,0.6)", border: "1px solid rgba(124,111,255,0.2)",
          borderRadius: 20, padding: "28px 32px", marginBottom: 24, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3,
            background: "linear-gradient(90deg,#7c6fff,#00d4aa)" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12,
              background: "rgba(124,111,255,0.15)", border: "1px solid rgba(124,111,255,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>◈</div>
            <div>
              <div style={{ fontSize: 9, color: "#7c6fff", fontWeight: 800,
                textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 4 }}>
                InvestorQ Tier B Certification
              </div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#e2e8f0", letterSpacing: "-0.02em" }}>
                Document Upload Portal
              </div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.7 }}>
            Welcome back, <strong style={{ color: "#94a3b8" }}>{orgData?.firstName || "there"}</strong>.
            Upload your documents for <strong style={{ color: "#e2e8f0" }}>{orgData?.orgName}</strong>'s
            Tier B certification. All documents are stored securely in InvestorQ's encrypted vault.
          </div>
          <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "#00d4aa",
              background: "rgba(0,212,170,0.1)", border: "1px solid rgba(0,212,170,0.25)",
              padding: "3px 10px", borderRadius: 20 }}>🔒 NDA Executed</span>
            <span style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8",
              background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
              padding: "3px 10px", borderRadius: 20 }}>⏱ Link expires {expiryDate}</span>
          </div>
        </div>

        {/* Document upload cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {DOC_TYPES.map(doc => (
            <div key={doc.key} style={{
              background: files[doc.key] ? "rgba(0,212,170,0.04)" : "rgba(30,41,59,0.5)",
              border: `1px solid ${files[doc.key] ? "rgba(0,212,170,0.3)" : "rgba(255,255,255,0.08)"}`,
              borderRadius: 14, padding: "20px 22px",
              transition: "all 0.2s",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <span style={{ fontSize: 24 }}>{doc.icon}</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0", marginBottom: 2 }}>
                      {doc.label}
                      {doc.required && (
                        <span style={{ fontSize: 9, color: "#f5a623", marginLeft: 8,
                          fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                          Required
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: "#475569" }}>{doc.hint}</div>
                  </div>
                </div>
                {files[doc.key] && (
                  <span style={{ fontSize: 10, color: "#00d4aa", fontWeight: 700 }}>✓ Ready</span>
                )}
              </div>

              {files[doc.key] ? (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "10px 14px", background: "rgba(0,212,170,0.08)",
                  border: "1px solid rgba(0,212,170,0.2)", borderRadius: 8 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 14 }}>📄</span>
                    <span style={{ fontSize: 11, color: "#94a3b8" }}>{files[doc.key].name}</span>
                  </div>
                  <button onClick={() => handleFileChange(doc.key, null)} style={{
                    background: "none", border: "none", color: "#475569",
                    cursor: "pointer", fontSize: 14, padding: 4 }}>✕</button>
                </div>
              ) : (
                <label style={{ display: "block", cursor: "pointer" }}>
                  <div style={{ border: "2px dashed rgba(255,255,255,0.1)", borderRadius: 10,
                    padding: "16px", textAlign: "center", transition: "all 0.2s" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(124,111,255,0.4)"}
                    onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"}>
                    <div style={{ fontSize: 20, marginBottom: 6, opacity: 0.4 }}>📂</div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>
                      Click to select file or drag and drop
                    </div>
                    <div style={{ fontSize: 10, color: "#334155", marginTop: 4 }}>
                      Accepted: {doc.accept} · Max 20MB
                    </div>
                  </div>
                  <input
                    type="file"
                    accept={doc.accept}
                    style={{ display: "none" }}
                    onChange={e => handleFileChange(doc.key, e.target.files[0] || null)}
                  />
                </label>
              )}
            </div>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div style={{ marginTop: 16, padding: "12px 16px", borderRadius: 10,
            background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)",
            fontSize: 12, color: "#f87171" }}>
            ⚠ {error}
          </div>
        )}

        {/* Submit */}
        <div style={{ marginTop: 24 }}>
          <button
            onClick={handleUpload}
            disabled={uploading}
            style={{ width: "100%", padding: 16, borderRadius: 14, border: "none",
              background: uploading ? "rgba(255,255,255,0.05)" : "linear-gradient(135deg,#7c6fff,#9b8fff)",
              color: uploading ? "#334155" : "#fff",
              fontSize: 14, fontWeight: 800, cursor: uploading ? "not-allowed" : "pointer",
              letterSpacing: "0.04em", transition: "all 0.2s",
              boxShadow: uploading ? "none" : "0 8px 32px rgba(124,111,255,0.4)" }}>
            {uploading ? "⏳ Uploading documents…" : "◈ Submit Documents for Certification"}
          </button>
          <div style={{ fontSize: 10, color: "#334155", textAlign: "center", marginTop: 10, lineHeight: 1.7 }}>
            Documents are encrypted and stored securely under your executed NDA.
            InvestorQ will review within 3-5 business days and email you when your tile is certified.
          </div>
        </div>
      </div>
    </PageShell>
  );
}

// ── Shell components ──────────────────────────────────────────────────────────
function PageShell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "#0a1223",
      fontFamily: "system-ui,-apple-system,sans-serif", color: "#fff" }}>
      {/* Nav bar */}
      <div style={{ background: "rgba(15,23,42,0.95)", backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "14px 24px",
        display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ fontSize: 18, fontWeight: 900, background: "linear-gradient(135deg,#7c6fff,#00d4aa)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          InvestorQ
        </div>
        <div style={{ fontSize: 9, color: "#475569", fontWeight: 700,
          textTransform: "uppercase", letterSpacing: "0.1em" }}>
          · Secure Document Portal
        </div>
      </div>
      {children}
    </div>
  );
}

function StatusCard({ icon, color, title, children }) {
  return (
    <div style={{ maxWidth: 480, margin: "60px auto", padding: "0 20px" }}>
      <div style={{ background: "rgba(30,41,59,0.6)", border: `1px solid ${color}33`,
        borderRadius: 20, padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{icon}</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: "#e2e8f0", marginBottom: 16 }}>{title}</div>
        <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.7 }}>{children}</div>
      </div>
    </div>
  );
}
